import { useState } from 'react';
import type { MeResponse } from '@lk/shared';
import { Button } from '../../components/Button';
import { humanMessage, isLoginFailed } from '../../lib/errors';
import { useParentLogin } from './useAuth';

/**
 * Вхід батька за поштою й паролем.
 *
 * Для батька це основний спосіб разом із Face ID: PIN-коду в нього більше
 * немає. Раніше був, і приймався тим самим полем, що й дитячий, — тобто
 * гаманець і вивід балів боронив короткий код, який боти перебирають.
 *
 * Схований за посиланням, бо на цьому екрані щодня заходять діти, а форма
 * пошти й пароля їм лише заважає. Дитині вона нічого не дає: пароль знають
 * тільки батьки.
 */
export function ParentPasswordLogin({
  onSuccess,
  onOpen,
  disabled,
}: {
  onSuccess: (me: MeResponse) => void;
  /** Викликається при розгортанні форми — щоб прибрати чужу помилку з екрана. */
  onOpen?: () => void;
  disabled?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const login = useParentLogin();

  if (!open) {
    return (
      <button
        type="button"
        className="mt-4 w-full cursor-pointer text-center text-xs font-bold text-white/35 underline-offset-4 hover:underline"
        onClick={() => {
          // Помилка про невірний PIN не має висіти над формою пароля:
          // дві червоні фрази одночасно — і незрозуміло, яка з них про що.
          onOpen?.();
          setOpen(true);
        }}
      >
        Вхід для батьків — пошта й пароль
      </button>
    );
  }

  const ready = email.includes('@') && password.length >= 8 && !disabled;

  return (
    <form
      className="mt-4 flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        login.mutate({ email: email.trim(), password }, { onSuccess });
      }}
    >
      <label className="text-xs font-black tracking-widest text-white/50 uppercase">
        Пошта батька
        <input
          type="email"
          autoComplete="email"
          inputMode="email"
          autoCapitalize="off"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="lk-panel-tight mt-1 h-12 w-full px-3 text-base font-bold outline-none"
        />
      </label>

      <label className="text-xs font-black tracking-widest text-white/50 uppercase">
        Пароль
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className="lk-panel-tight mt-1 h-12 w-full px-3 text-base font-bold outline-none"
        />
      </label>

      <Button type="submit" tone="ghost" block mute loading={login.isPending} disabled={!ready}>
        Увійти паролем
      </Button>

      {login.isError ? (
        <p className="text-center text-sm font-bold text-[var(--lk-red)]" role="alert">
          {isLoginFailed(login.error) ? 'Пошта або пароль не збігаються.' : humanMessage(login.error)}
        </p>
      ) : null}

      <button
        type="button"
        className="cursor-pointer text-center text-xs font-bold text-white/35 underline-offset-4 hover:underline"
        onClick={() => {
          setOpen(false);
          setPassword('');
          login.reset();
        }}
      >
        Назад до PIN
      </button>
    </form>
  );
}
